import {useAtom} from "jotai";
import {useCallback, useEffect} from "react";
import {FlowProtectionSessionsAtom} from "../pages/project/atoms";

const STORAGE_KEY = "flowProtectionSessions";

const readStorage = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
  } catch (e) {
    return {};
  }
};

const writeStorage = (sessions) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
  } catch (e) {
    // ignore write errors (e.g. private browsing quota)
  }
};

const DEFAULT_TTL_SECONDS = 900; // 15 minutes, matches backend session length

/**
 * Tracks the per-flow "protection unlock" session token used to view
 * protected user messages unmasked. Backed by an in-memory jotai atom
 * (for reactivity across mounted components) mirrored into sessionStorage
 * (so a refresh within the same tab doesn't force re-entering the password).
 * The token itself is never persisted beyond the browser session/tab.
 */
export const useFlowProtection = (flowUid) => {
  const [sessions, setSessions] = useAtom(FlowProtectionSessionsAtom);

  // Hydrate this flow's session from sessionStorage the first time it's used.
  useEffect(() => {
    if (!flowUid || sessions[flowUid]) return;
    const entry = readStorage()?.[flowUid];
    if (entry?.token && entry?.expiresAt > Date.now()) {
      setSessions(prev => ({...prev, [flowUid]: entry}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowUid]);

  const session = flowUid ? sessions[flowUid] : null;
  const isUnlocked = !!(session?.token && session?.expiresAt > Date.now());

  const unlock = useCallback((token, expiresInSeconds = DEFAULT_TTL_SECONDS) => {
    if (!flowUid || !token) return;
    setSessions(prev => {
      const next = {...prev, [flowUid]: {token, expiresAt: Date.now() + (expiresInSeconds || DEFAULT_TTL_SECONDS) * 1000}};
      writeStorage(next);
      return next;
    });
  }, [flowUid, setSessions]);

  // Call after a request successfully used the token - the backend slides
  // the real session forward, this keeps the local expiry in sync.
  const extend = useCallback((expiresInSeconds = DEFAULT_TTL_SECONDS) => {
    if (!flowUid) return;
    setSessions(prev => {
      if (!prev[flowUid]?.token) return prev;
      const next = {...prev, [flowUid]: {...prev[flowUid], expiresAt: Date.now() + (expiresInSeconds || DEFAULT_TTL_SECONDS) * 1000}};
      writeStorage(next);
      return next;
    });
  }, [flowUid, setSessions]);

  const lock = useCallback(() => {
    if (!flowUid) return;
    setSessions(prev => {
      if (!(flowUid in prev)) return prev;
      const next = {...prev};
      delete next[flowUid];
      writeStorage(next);
      return next;
    });
  }, [flowUid, setSessions]);

  return {
    token: isUnlocked ? session.token : null,
    isUnlocked,
    expiresAt: session?.expiresAt || null,
    unlock,
    extend,
    lock,
  };
};
